import { DateTime } from 'luxon';
import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { io, Socket as SocketIOAlias } from 'socket.io-client';
import { TravelerInfo } from '../components/flight/breakdown-summary/travelerInfoValidation';
import { PolicyFlagView } from '../components/policy-flags/types';
import { EnvConfig } from '../config/configMapping';
import {
  BookedSeatItem,
  BookedSeatsAddedPayload,
  BookedSeatsSummaryPayload,
  CancellationCreditItem,
  CancellationSummaryPayload,
  DefaultAccommodationDetailsOfferFragment,
  DefaultFlightDetailsOfferFragment,
  DefaultTravelAgentBasketFragment,
  DefaultVehicleOfferFragment,
  FlightExchangeLegOption,
  FlightExchangeLegSelectionPayload,
  FlightExchangeSummaryPayload,
  HotelExchangeAccommodationSelectionPayload,
  HotelExchangeOperationPayload,
  HotelExchangeSummaryPayload,
  LocationCoordinates,
  OfferSorting,
  TravelAgentState,
  TravelMessageType,
  TripComponentSelectionPayload,
  TripQuestionsPayload,
  TripSelectionPayload,
} from '../graphql/generated/graphql';
import { logger } from '../initialisers/logger';

/** Same key the AuthKitProvider + Apollo authLink read. Re-declared here to keep this provider's
 * dependency surface minimal — Socket.IO's `auth` callback runs outside React. */
const BYPASS_TOKEN_KEY = 'altitude:auth:bypass:token';

/** The like-for-like half of a priced day: the same airline as the top card, departing around the same hour. */
export type AgentAlternateDateSimilar = {
  /** Whether that airline could be priced near that time on that day at all. */
  status: 'available' | 'unavailable';
  /** Lowest total for this leg on that day within the comparison window; absent when unavailable. */
  legPrice?: {
    /** The total price for the leg on that day. */
    amount: number;
    /** Currency of the amount, ISO 4217. */
    currency: string;
  };
  /** Validating carrier of the matched itinerary, so a day can be checked against the card it was matched to. */
  airlineIataCode?: string;
  /** Trading name of that carrier, so the strip names the airline the way the cards do; absent when the code is unknown to us. */
  airlineName?: string;
  /** Origin-local departure clock (HH:mm) of the matched itinerary, so the strip says how close the match departs. */
  departureTime?: string;
  /** True when this fare is on the carrier the day's cheapest one already names, so the strip prints one row not two. */
  sameAirlineAsCheapest?: boolean;
};

/** The card the searched day is answered with: a whole-trip price the one-leg strip cannot see. */
export type AgentAlternateDateBestValue = {
  /** What the card charges for the whole trip; the strip prints it as it is, with no baseline added. */
  tripTotal: {
    /** The card's whole-trip amount. */
    amount: number;
    /** Currency of the amount, ISO 4217. */
    currency: string;
  };
  /** Marketing carrier of the card's flight, so the day names the airline the cards name. */
  airlineIataCode?: string;
  /** Trading name of that carrier; absent when the offer carried no name for the code. */
  airlineName?: string;
  /** Origin-local departure clock (HH:mm) of the card's flight. */
  departureTime?: string;
  /** Id of the bundle the card books, so the day can be tied back to the card it came from. */
  offerId: string;
};

/** One priced day of the agent's flexible-date strip. The agent sends its own JSON, whose status is lower case, not the
 * SCREAMING_CASE of the manual search enum. */
export type AgentAlternateDateOption = {
  /** The day this price belongs to (YYYY-MM-DD, origin local). */
  departureDate: string;
  /** Whether the provider could price the leg on that day at all. */
  status: 'available' | 'unavailable';
  /** That day's lowest total for this leg alone; absent when the day is unavailable. */
  legPrice?: {
    /** The total price for the leg on that day. */
    amount: number;
    /** Currency of the amount, ISO 4217. */
    currency: string;
  };
  /** Validating carrier of the itinerary that day's price comes from; absent when the day is unavailable. */
  airlineIataCode?: string;
  /** Trading name of that carrier, so the strip names the airline the way the cards do; absent when the code is unknown to us. */
  airlineName?: string;
  /** Origin-local departure clock (HH:mm) of the flight that day's price comes from; absent when the day is unavailable. */
  departureTime?: string;
  /** That day priced like the top card instead of at its cheapest; absent when the comparison shop never ran. */
  similar?: AgentAlternateDateSimilar;
  /** The best value on screen for this day; set only on the day the presented cards were searched for. */
  bestValue?: AgentAlternateDateBestValue;
  /** The top ranked card on screen for this day, what a strip quoting recommended fares prints; set only on the day the presented cards were searched for. */
  recommended?: AgentAlternateDateBestValue;
};

export type TravelAgentFlightPresentationResponse = {
  /** The type of response */
  responseType: TravelMessageType.FlightPresentation;
  /** The flight offers */
  flightOffersSearched: DefaultFlightDetailsOfferFragment[];
  /** Lowest fare per day around the presented leg's date, ascending; empty unless the traveller gave a date window. */
  alternateDates?: AgentAlternateDateOption[];
  /** What the trip's other still-open legs cost at their cheapest, so every strip day quotes the whole trip the cards quote. */
  alternateDatesBaseline?: number;
  /** How the presented leg was ranked when its days were priced: price means the traveller asked for the cheapest, so the strip quotes each day's lowest fare rather than the fare of the top card. */
  alternateDatesSorting?: OfferSorting;
  /** Median grand total across the presented offers — drives the out-of-policy explainer. */
  medianPrice?: number;
  /** Zero-based index of the leg being presented; >0 means a price delta vs the locked leg(s) applies. */
  legIndex?: number;
  /** Total legs in the whole trip (1 = one way, 2 = round trip, 3+ = multi-city) — labels the card trip-type even on the first leg. */
  totalLegs?: number;
  /** The response message from the AI agent */
  message: string | undefined;
  /** The conversation ID provided to the client and to provide to the client */
  conversationId: string;
};

export type TravelAgentAddingPaymentMethodResponse = {
  /** The type of response */
  responseType: TravelMessageType.AddingPaymentMethod;
  /** The response message from the AI agent */
  message: string | undefined;
};

/** Who the passport picker is for; sent by the backend only when that is somebody other than the booker. */
export type PassportTraveler = {
  /** Id the passport belongs to — a passport added from the picker is filed against this user, not the booker */
  userId: string;
  /** Display name, so the picker can name the traveller in its heading and empty state */
  name: string;
};

export type TravelAgentValidatingPassportResponse = {
  /** The type of response */
  responseType: TravelMessageType.ValidatingPassport;
  /** The response message from the AI agent */
  message: string | undefined;
  /** Available passports for selection */
  passports: Array<{
    id: string;
    givenName: string;
    lastName: string;
    nationality: string;
    numberLast4: string;
    expiryDate: string;
    isExpired: boolean;
  }>;
  /** Set when booking on behalf, so the picker targets the traveller instead of the signed-in booker */
  traveler?: PassportTraveler;
};

export type TravelAgentValidatingBookingInformationResponse = {
  /** The type of response */
  responseType: TravelMessageType.ValidatingBookingInformation;
  /** The response message from the AI agent */
  message: string | undefined;
  /** The traveler information (partial when the agent is collecting missing profile fields) */
  travelerInfo: Omit<TravelerInfo, 'dateOfBirth'> & {
    dateOfBirth: DateTime | string | undefined;
  };
};

export type BaseTravelAgentResponse = {
  /** The conversation ID provided to the client and to provide to the client */
  conversationId: string;
  /**
   * The latest updated basket, absent when the turn could not read it. The conversation replaces its
   * cart with whatever arrives here, so a missing basket must leave the current one alone.
   */
  basket?: DefaultTravelAgentBasketFragment;
  /** The interrupt ID */
  interruptId: string | undefined;
  /**
   * Id of an interrupt this message supersedes. The conversation drops the card carrying that id instead of
   * stacking a second copy: a question re-asked for a new traveller replaces the one it re-asks.
   */
  replacesInterruptId?: string;
};

/** A geographic area to avoid, displayed as a red polygon overlay on the accommodation map. */
export type AvoidAreaPolygon = {
  /** Human-readable name of the area */
  name: string;
  /** Why the user wants to avoid this area */
  reason: string;
  /** Approximate boundary as an array of lat/lng vertices */
  polygon: Array<{ lat: number; lng: number }>;
};

export type TravelAgentAccommodationPresentationResponse = {
  /** The type of response */
  responseType: TravelMessageType.AccommodationPresentation;
  /** The accommodation offers */
  accommodationOffersSearched: DefaultAccommodationDetailsOfferFragment[];
  /** The user destination */
  userDestination: LocationCoordinates | undefined;
  /** Avoid-area polygons from semantic location resolution, rendered as red zones on the map */
  avoidAreas?: AvoidAreaPolygon[];
  /** The response message from the AI agent */
  message: string | undefined;
};

/** The agent is showing rental cars to pick from; a pick resumes the interrupt with the car's offer id. */
export type TravelAgentVehiclePresentationResponse = {
  /** The type of response */
  responseType: TravelMessageType.VehiclePresentation;
  /** The cars on the card, ranked, each with the car policies it was checked against */
  vehicleOffersSearched: DefaultVehicleOfferFragment[];
  /** Where and when the cars were searched for, and what was assumed, so the card is read in its context */
  message: string | undefined;
};

/**
 * One flight the seat picker draws a tab for.
 *
 * Deliberately without its cabin: the maps are fetched with `flightBundleSeatMaps` (basket) or
 * `bookedFlightSeatMaps` (booked trip), so nothing has to hand-shape a seat map onto the socket.
 */
export type SeatPickerLeg = {
  /** Position in the picker, which is what a pick is keyed by on the way back */
  legIndex: number;
  /** e.g. "SYD to MEL, Mon 12 Oct" */
  label: string;
  /** What this leg already holds, so the picker opens on the seat the traveller has */
  selectedSeats: { segmentId: string; designator: string }[];
  /** True when the fare says a standard seat on this leg costs nothing */
  included: boolean;
  /** Set on a booked-trip picker only: the booked flight whose maps this leg draws */
  tripFlightId?: string;
};

export type TravelAgentSeatMapPresentationResponse = {
  /** The type of response */
  responseType: TravelMessageType.SeatMapPresentation;
  /** Whether the picker is seating the basket or a trip that is already booked */
  target: 'basket' | 'bookedTrip';
  /** The stored bundle a basket picker reads its maps from */
  bundleId?: string;
  /** The booked trip a post-booking picker reads its maps from */
  tripId?: string;
  /** One entry per flight the traveller can seat */
  legs: SeatPickerLeg[];
  /** The leg the picker opens on */
  focusLegIndex: number;
  /** The response message from the AI agent */
  message: string | undefined;
};

/** One seat on the booked-trip confirmation card, priced by the airline. */
export type SocketBookedSeatItem = Omit<BookedSeatItem, '__typename'>;

/** The seats and total awaiting the traveller's yes, as the socket sends them. */
export type SocketBookedSeatsSummary = Omit<BookedSeatsSummaryPayload, 'flights' | 'seats' | '__typename'> & {
  /** The seats about to be assigned, priced from the airline */
  seats: SocketBookedSeatItem[];
  /**
   * The flights being seated, in the fragment the SPA reads them with.
   *
   * The server type would be the wider `FlightDetailsOffer`, whose `__typename`s are optional; the basket's own
   * flight row is written against the fragment, which is what the socket actually sends here.
   */
  flights: DefaultFlightDetailsOfferFragment[];
};

/** The seats the airline has now confirmed, as the socket sends them. */
export type SocketBookedSeatsAdded = Omit<BookedSeatsAddedPayload, 'seats' | '__typename'> & {
  /** The seats that were added, in the words the banner names them */
  seats: SocketBookedSeatItem[];
};

/** The seat add went through: the airline holds the seats and the card has been charged. */
export type TravelAgentBookedSeatsAddedResponse = {
  /** The type of response */
  responseType: TravelMessageType.BookedSeatsAdded;
  /** The seats, the trip they are on and what was taken for them */
  bookedSeatsAdded: SocketBookedSeatsAdded;
  /** The response message from the AI agent */
  message: string | undefined;
};

/** The agent is holding a booked-trip seat add until the traveller confirms the seats and the total. */
export type TravelAgentBookedSeatsConfirmationResponse = {
  /** The type of response */
  responseType: TravelMessageType.BookedSeatsConfirmation;
  /** The seats, their prices and the card that will be charged */
  bookedSeatsSummary: SocketBookedSeatsSummary;
  /** The response message from the AI agent */
  message: string | undefined;
};

export type TravelAgentTripConfirmationResponse = {
  /** The type of response */
  responseType: TravelMessageType.TripConfirmation;
  /** The response message from the AI agent */
  message: string;
};

/** Agent-driven request for the user's OOP justification — surfaces the same flags the approval card lists. */
export type TravelAgentOutOfPolicyReasonRequestResponse = {
  /** The type of response */
  responseType: TravelMessageType.OutOfPolicyReasonRequest;
  /** The response message from the AI agent */
  message: string;
  /** Deduped policy-violation flags from the selected offers, rendered as the approval-style PolicyFlagsPanel in the chat bubble. */
  policyFlags: PolicyFlagView[];
};

export type TravelAgentConversationResponse = {
  /** The type of message */
  responseType: TravelMessageType.Conversation;
  /** The response message from the AI agent */
  message: string;
};

/**
 * The action row a day picked off the fare strip leaves in the conversation.
 *
 * The pick is a click, not a sentence, so it produces no chat bubble; the transcript keeps this row instead, and a
 * reloaded conversation replays it from the stored interrupt result so the moved date still has something saying why.
 */
export type TravelAgentAlternateDateSearchResponse = {
  /** The type of response */
  responseType: TravelMessageType.AlternateDateSearch;
  /** The label the transcript stored for this action; the row formats its own text from the day. */
  message: string | undefined;
  /** The day the pick re-searched the leg on (YYYY-MM-DD), which the row names. */
  alternateDateSearched: string;
};

/**
 * A credit line as it arrives over the socket. The GraphQL reload path runs `expiresAt` through Apollo's DateTime
 * field policy and hands back a Luxon DateTime; the live socket bypasses Apollo, so the value is still an ISO string.
 */
export type SocketCancellationCreditItem = Omit<CancellationCreditItem, 'expiresAt' | '__typename'> & {
  /** ISO timestamp of when the credit stops being actionable; absent when the airline policy is unknown */
  expiresAt: string | undefined;
};

/** The refund breakdown as it arrives over the socket; only the credit dates differ from the GraphQL shape. */
export type SocketCancellationSummary = Omit<CancellationSummaryPayload, 'credits' | '__typename'> & {
  /** Credits the cancellation would create, with their expiry still as an ISO string */
  credits: SocketCancellationCreditItem[];
};

/** The agent needs to know which trip the request is about, so it offers the traveller's trips to pick from. */
export type TravelAgentTripSelectionResponse = {
  /** The type of response */
  responseType: TravelMessageType.TripSelection;
  /** The trips offered, and the flow the picked trip is fed back into */
  tripSelection: TripSelectionPayload;
  /** The response message from the AI agent */
  message: string | undefined;
};

/** The agent has found the trip and is asking how much of it the cancellation should cover. */
export type TravelAgentTripComponentSelectionResponse = {
  /** The type of response */
  responseType: TravelMessageType.TripComponentSelection;
  /** Everything booked on the trip, priced and carrying what each part gives back */
  tripComponentSelection: TripComponentSelectionPayload;
  /** The response message from the AI agent */
  message: string | undefined;
};

/** The agent is checking its guesses about a new trip (where from, when back, hotel) before the first search. */
export type TravelAgentTripQuestionsResponse = {
  /** The type of response */
  responseType: TravelMessageType.TripQuestions;
  /** The questions, each with the guess preselected, and the summary line they build */
  tripQuestions: TripQuestionsPayload;
  /** The sentence above the card, saying why the agent asks */
  message: string | undefined;
};

/** The agent is holding a cancellation until the traveller confirms the refund it would produce. */
export type TravelAgentCancellationConfirmationResponse = {
  /** The type of response */
  responseType: TravelMessageType.CancellationConfirmation;
  /** Per-component refund breakdown of the cancellation awaiting confirmation */
  cancellationSummary: SocketCancellationSummary;
  /** The response message from the AI agent */
  message: string | undefined;
};

/**
 * A leg row as it arrives over the socket. The GraphQL reload path runs the leg's datetimes through Apollo's
 * DateTime field policy and hands back Luxon DateTimes; the live socket bypasses Apollo, so they are still ISO
 * strings. Both wires state them in UTC, which is why the clock the row prints rides along as its own label.
 */
export type SocketFlightExchangeLegOption = Omit<
  FlightExchangeLegOption,
  'departureDatetime' | 'arrivalDatetime' | '__typename'
> & {
  /** ISO timestamp of the leg's first segment departure; absent when the booking carries no usable departure */
  departureDatetime?: string;
  /** ISO timestamp of the leg's last segment arrival; absent when the booking carries no usable arrival */
  arrivalDatetime?: string;
};

/**
 * The leg picker as it arrives over the socket. The GraphQL reload path runs `expiresAt` through Apollo's
 * DateTime field policy and hands back a Luxon DateTime; the live socket bypasses Apollo, so it is still an
 * ISO string.
 */
export type SocketFlightExchangeLegSelection = Omit<
  FlightExchangeLegSelectionPayload,
  'expiresAt' | 'legs' | '__typename'
> & {
  /** ISO timestamp of the earliest changeable leg's departure; absent when nothing on the card is changeable */
  expiresAt?: string;
  /** Every leg of the trip's ticketed flights, with their departures still as ISO strings */
  legs: SocketFlightExchangeLegOption[];
};

/** The change quote as it arrives over the socket; only the legs' departures differ from the GraphQL shape. */
export type SocketFlightExchangeSummary = Omit<
  FlightExchangeSummaryPayload,
  'keptLegs' | 'replacedLeg' | 'newLeg' | '__typename'
> & {
  /** Legs the exchange leaves alone, with their departures still as ISO strings */
  keptLegs: SocketFlightExchangeLegOption[];
  /** The leg being given up, with its departure still as an ISO string */
  replacedLeg: SocketFlightExchangeLegOption;
  /** The leg replacing it, with its departure still as an ISO string; absent on a drop, which sells nothing */
  newLeg?: SocketFlightExchangeLegOption | null;
};

/** The agent needs to know which leg of a booked trip moves, so it offers the trip's legs to pick from. */
export type TravelAgentFlightExchangeLegSelectionResponse = {
  /** The type of response */
  responseType: TravelMessageType.FlightExchangeLegSelection;
  /** The legs offered, and the trip they belong to */
  flightExchangeLegSelection: SocketFlightExchangeLegSelection;
  /** The response message from the AI agent */
  message: string | undefined;
};

/** The agent is holding an exchange until the traveller confirms the change cost it would charge. */
export type TravelAgentFlightExchangeConfirmationResponse = {
  /** The type of response */
  responseType: TravelMessageType.FlightExchangeConfirmation;
  /** What the change costs, line by line, awaiting confirmation */
  flightExchangeSummary: SocketFlightExchangeSummary;
  /** The response message from the AI agent */
  message: string | undefined;
};

/** The exchange went through: the ticket was reissued and the difference charged. */
export type TravelAgentFlightExchangedResponse = {
  /** The type of response */
  responseType: TravelMessageType.FlightExchanged;
  /** The trip whose flight was reissued, so the banner can deep-link the updated itinerary */
  tripId?: string;
  /** The response message from the AI agent */
  message: string | undefined;
};

/**
 * The booking picker as it arrives over the socket. The GraphQL reload path runs `expiresAt` through Apollo's
 * DateTime field policy and hands back a Luxon DateTime; the live socket bypasses Apollo, so it is still an
 * ISO string.
 */
export type SocketHotelAccommodationSelection = Omit<
  HotelExchangeAccommodationSelectionPayload,
  'expiresAt' | '__typename'
> & {
  /** ISO check out of the earliest exchangeable booking; absent when nothing on the card can be exchanged */
  expiresAt?: string;
};

/** The replace or add question as it arrives over the socket; every field of it is already printed text. */
export type SocketHotelExchangeOperation = Omit<HotelExchangeOperationPayload, '__typename'>;

/** The exchange quote as it arrives over the socket; `quotedAt` bypasses Apollo, so it is still an ISO string. */
export type SocketHotelExchangeSummary = Omit<HotelExchangeSummaryPayload, 'quotedAt' | '__typename'> & {
  /** ISO moment the quote was priced at, echoed back on the exchange */
  quotedAt?: string;
};

/** The agent needs to know which booking an exchange is about, so it offers the trip's bookings to pick from. */
export type TravelAgentHotelAccommodationSelectionResponse = {
  /** The type of response */
  responseType: TravelMessageType.HotelAccommodationSelection;
  /** The bookings offered, and the trip they belong to */
  hotelAccommodationSelection: SocketHotelAccommodationSelection;
  /** The response message from the AI agent */
  message: string | undefined;
};

/** The words fitted both operations, so the agent asks which one the traveller meant before it searches. */
export type TravelAgentHotelExchangeOperationResponse = {
  /** The type of response */
  responseType: TravelMessageType.HotelExchangeOperation;
  /** The booking in question and the two options, in the order the card lists them */
  hotelExchangeOperation: SocketHotelExchangeOperation;
  /** The response message from the AI agent */
  message: string | undefined;
};

/** The agent is holding an exchange until the traveller confirms what it does and what it costs. */
export type TravelAgentHotelExchangeConfirmationResponse = {
  /** The type of response */
  responseType: TravelMessageType.HotelExchangeConfirmation;
  /** What the exchange costs, line by line, awaiting confirmation */
  hotelExchangeSummary: SocketHotelExchangeSummary;
  /** The response message from the AI agent */
  message: string | undefined;
};

/** The exchange went through: the new booking is made and the original handled as the traveller chose. */
export type TravelAgentHotelExchangedResponse = {
  /** The type of response */
  responseType: TravelMessageType.HotelExchanged;
  /** The trip whose booking was exchanged, so the banner can deep-link the updated itinerary */
  tripId?: string;
  /** The response message from the AI agent */
  message: string | undefined;
};

/** The cancellation went through: the whole trip is cancelled and the refund is on its way. */
export type TravelAgentTripCancelledResponse = {
  /** The type of response */
  responseType: TravelMessageType.TripCancelled;
  /** The trip that was cancelled, so the banner can deep-link the itinerary */
  tripId?: string;
  /** The response message from the AI agent */
  message: string | undefined;
};

export type TravelAgentTripBookedResponse = {
  /** The type of response */
  responseType: TravelMessageType.TripBooked;
  /** The booking ID */
  bookingId: string;
  /** The response message from the AI agent */
  message: string;
};

/** Trip created in PendingApproval — booking is queued behind an admin's decision. */
export type TravelAgentTripAwaitingApprovalResponse = {
  /** The type of response */
  responseType: TravelMessageType.TripAwaitingApproval;
  /** The booking ID */
  bookingId: string;
  /** The response message from the AI agent (kept empty by the backend; the SPA banner is the user-facing message) */
  message: string;
};

export type SocketTravelAgentResponse = BaseTravelAgentResponse &
  (
    | TravelAgentFlightPresentationResponse
    | TravelAgentAccommodationPresentationResponse
    | TravelAgentVehiclePresentationResponse
    | TravelAgentConversationResponse
    | TravelAgentAlternateDateSearchResponse
    | TravelAgentSeatMapPresentationResponse
    | TravelAgentTripConfirmationResponse
    | TravelAgentOutOfPolicyReasonRequestResponse
    | TravelAgentTripBookedResponse
    | TravelAgentTripAwaitingApprovalResponse
    | TravelAgentValidatingBookingInformationResponse
    | TravelAgentAddingPaymentMethodResponse
    | TravelAgentValidatingPassportResponse
    | TravelAgentTripSelectionResponse
    | TravelAgentTripComponentSelectionResponse
    | TravelAgentTripQuestionsResponse
    | TravelAgentCancellationConfirmationResponse
    | TravelAgentBookedSeatsConfirmationResponse
    | TravelAgentBookedSeatsAddedResponse
    | TravelAgentFlightExchangeLegSelectionResponse
    | TravelAgentFlightExchangeConfirmationResponse
    | TravelAgentFlightExchangedResponse
    | TravelAgentHotelAccommodationSelectionResponse
    | TravelAgentHotelExchangeOperationResponse
    | TravelAgentHotelExchangeConfirmationResponse
    | TravelAgentHotelExchangedResponse
    | TravelAgentTripCancelledResponse
  );

/** A traveller's passport as shown in the agent basket passport picker (mirrors the backend PassportSummary). */
export type PassportSummary = {
  /** Database ID of the passport */
  id: string;
  /** Given name on the passport */
  givenName: string;
  /** Middle name(s) on the passport; empty when the holder has none */
  middleName?: string;
  /** Last name on the passport */
  lastName: string;
  /** Two-letter ISO country code of the holder's nationality */
  nationality: string;
  /** Last 4 characters of the document number */
  numberLast4: string;
  /** Expiry date in ISO format */
  expiryDate: string;
  /** Whether the passport is expired as of today */
  isExpired: boolean;
};

export type ServerToClientEvents = {
  /** Response from the Travel Agent */
  'travel-agent:response': (input: SocketTravelAgentResponse) => void;
  /** State of the Travel Agent */
  'travel-agent:state': (input: { state: TravelAgentState }) => void;
  /**
   * The prose reply as the model writes it. `start` means "discard any provisional text"; the
   * `travel-agent:response` that follows is authoritative and replaces the provisional bubble.
   */
  'travel-agent:reply-stream': (input: { type: 'start' } | { type: 'chunk'; text: string }) => void;
  /** current state for the basket */
  'travel-agent:basket': (input: DefaultTravelAgentBasketFragment) => void;
  /** Booking confirmed notification to the user */
  'travel-agent:booking-confirmed': (input: {
    bookingId: string;
    /** Drives the post-booking banner: TripBooked = success, TripAwaitingApproval = queued behind an approver. */
    responseType: TravelMessageType.TripBooked | TravelMessageType.TripAwaitingApproval;
  }) => void;
  /** The flight change in the cart went through: the ticket was reissued and the difference charged */
  'travel-agent:flight-exchanged': (input: {
    /** The trip whose flight was reissued, so the banner can deep-link the updated itinerary. */
    tripId: string;
    /** What the traveller is told happened, the same copy the conversational path writes. */
    message: string;
    /** Always FlightExchanged; the banner branches on the type rather than on the event name. */
    responseType: TravelMessageType.FlightExchanged;
  }) => void;
  /** Error message to the user */
  'travel-agent:error-message': (input: { errorMessage: string }) => void;
  /**
   * Outcome of a "Booking for" change. The basket shows the new traveller optimistically, so it needs telling
   * whether the agent took it: `userToBookForId` is who the conversation is for now, applied or reverted.
   */
  'travel-agent:book-for-user': (input: { applied: boolean; userToBookForId?: string }) => void;
  /** Missing traveler info to the user */
  'travel-agent:missing-traveler-info': (input: { travelerInfo: TravelerInfo }) => void;
  /** International flight has no passport selected — prompts the SPA to show the passport picker */
  'travel-agent:missing-passport': (input: { passports: PassportSummary[]; traveler?: PassportTraveler }) => void;
  /** Reporting: incremental state progress for a pending report cell */
  'report:state': (input: { cellId: string; state: 'thinking' | 'querying' | 'rendering' | 'done' | 'error' }) => void;
  /** Reporting: final rendered spec for a completed report cell.
   * `spec` is `unknown` here because raw socket payloads are unvalidated at the
   * transport layer; the notebook re-validates via `parseRenderSpec` on receipt. */
  'report:cell': (input: { cellId: string; spec: unknown }) => void;
  /** Reporting: terminal error for a report cell */
  'report:error': (input: { cellId: string; message: string }) => void;
};

/** Base parameters for all message events */
type BaseMessageParams = {
  /** Conversation ID */
  conversationId: string | undefined;
  /** Interrupt ID */
  interruptId: string | undefined;
  /** Latitude from the browser location (very precise) */
  latitude?: number;
  /** Longitude from the browser location (very precise) */
  longitude?: number;
};

type ClientToServerEvents = BaseMessageParams & {
  /** User send a message to the Travel Agent */
  'travel-agent:message': (
    input: BaseMessageParams & {
      message: string;
      /** Seats clicked on a live picker card and not confirmed, sent with the sentence typed instead of Confirm */
      seatPicks?: { seats: { legIndex: number; segmentId: string; designator: string }[]; clearedLegIndexes: number[] };
    },
  ) => void;
  /** When user interacts with an interrupt */
  'travel-agent:resume': (input: BaseMessageParams & Record<string, unknown>) => void;
  /** User removed an item from the basket */
  'travel-agent:basket:remove-item': (input: BaseMessageParams & { offerId: string }) => void;
  /** User picked or cleared the seat on one basket leg, without spending a conversation turn on it */
  'travel-agent:basket:set-seat': (
    input: BaseMessageParams & { legIndex: number; seats: { segmentId: string; designator: string }[] },
  ) => void;
  /** User booked a trip from the basket */
  'travel-agent:basket:book': (
    input: BaseMessageParams & {
      travelerInfo?: TravelerInfo;
      paymentMethodId?: string;
      /** Passport chosen after a `travel-agent:missing-passport` prompt; re-emitted so the booking attaches it. */
      userDocumentId?: string;
      /** OOP justification captured from the OutOfPolicyApprovalModal opened before this event was emitted. */
      approvalUserReason?: string;
    },
  ) => void;
  /**
   * User confirmed, from the cart, the flight change the agent put there. Runs the same exchange the chat
   * confirmation runs, so the cart button is never a second, weaker path to the same reissue.
   */
  'travel-agent:basket:confirm-change': (input: BaseMessageParams & { paymentMethodId?: string }) => void;
  /** User selected a payment method — persists it in the agent state */
  'travel-agent:set-payment-method': (input: BaseMessageParams & { paymentMethodId?: string }) => void;
  /** User picked who the agent is booking for — persists it in the agent state */
  'travel-agent:set-book-for-user': (input: BaseMessageParams & { userToBookForId?: string }) => void;
  /** Reporting: user submits a natural-language prompt for a new report cell */
  'report:message': (input: {
    cellId: string;
    prompt: string;
    /** Accumulated context from prior completed cells — prompt + insight only, never SQL or raw rows. */
    priorContext: { prompt: string; insight: string }[];
  }) => void;
};

type SocketIOClient = SocketIOAlias<ServerToClientEvents, ClientToServerEvents>;

export type SocketProviderState = Readonly<{
  connect: () => void;
  disconnect: () => void;
  /**
   * Runs the handshake again on a live connection. The handshake resolves the user once, so after an act-as
   * a socket left as it was would keep pushing the previous company's events.
   */
  reconnect: () => void;
  socket: SocketIOClient;
  isConnected: boolean;
}>;

export const SocketContext = createContext<SocketProviderState | undefined>(undefined);

/**
 * A socket a Cypress spec installs on `window` in place of the real one.
 *
 * The conversation is driven by a model, so an end-to-end spec that talks to the live agent tests the model's mood
 * as much as the page. This lets a spec push the exact response payloads the page has to render and read back the
 * events it emits, leaving the agent itself to the evals and the LLM tests.
 */
declare global {
  interface Window {
    /** Typed loosely on purpose: a spec installs the emitter half of a socket, not a real connection. */
    __altitudeE2eSocket?: unknown;
  }
}

export const SocketProvider: FC<{ envConfig: EnvConfig; children: React.ReactNode }> = ({ envConfig, children }) => {
  const [isConnected, setIsConnected] = useState(false);

  /**
   * Setup the socket, does not connect by default
   */
  const socket: SocketIOClient = useMemo(() => {
    // Test-only, and only in the e2e build: a spec that has installed a stub gets it instead of a live connection.
    // Same gate the card form uses to skip Stripe, so no production path can reach it.
    if (envConfig.environment === 'e2e' && window.__altitudeE2eSocket) {
      return window.__altitudeE2eSocket as SocketIOClient;
    }

    return io(envConfig.socketIO.uri, {
      autoConnect: false,
      path: '/socket',
      // Carry the HttpOnly session cookie alongside the websocket handshake. Backend Socket.IO
      // CORS reflects the SPA origin with `credentials: true`. The bypass token in `auth` is
      // only meaningful for `auth.bypass` mode (Cypress / integration tests).
      withCredentials: true,
      auth: (cb) => {
        cb({ token: localStorage.getItem(BYPASS_TOKEN_KEY) });
      },
      transports: ['websocket'],
      upgrade: false,
    });
  }, [envConfig.socketIO.uri, envConfig.environment]);

  useEffect(() => {
    const handleConnect = () => {
      logger.debug('Socket is connected');
      setIsConnected(true);
    };

    const handleDisconnect = () => {
      logger.debug('Socket is disconnected');
      setIsConnected(false);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket]);

  /**
   * Connect to the socket
   */
  const connect = useCallback(() => {
    socket.connect();
  }, [socket]);

  /**
   * Disconnect to the socket
   */
  const disconnect = useCallback(() => {
    socket.disconnect();
  }, [socket]);

  /**
   * Reconnect only a live socket: a page that has not connected yet will run a fresh handshake when it does.
   */
  const reconnect = useCallback(() => {
    if (!socket.connected) {
      return;
    }
    socket.disconnect();
    socket.connect();
  }, [socket]);

  const [values] = useMemo(
    () => [
      {
        connect,
        socket,
        disconnect,
        reconnect,
        isConnected,
      },
    ],
    [socket, connect, disconnect, reconnect, isConnected],
  );

  return <SocketContext.Provider value={values}>{children}</SocketContext.Provider>;
};

/**
 * This can be used in the application when you are not sure the user is present
 */
export const useSocket = (): SocketProviderState => {
  const context = useContext(SocketContext);

  if (context === undefined) {
    throw new Error(`useSocket must be used within an SocketProvider`);
  }

  return {
    ...context,
  };
};
